'use client';

import {
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Pagination as PaginationRoot,
} from '@workspace/ui/components/pagination';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select';
// import { useTranslations } from "next-intl";
import { cn, DEFAULT_PAGE_SIZE_OPTIONS } from '@workspace/ui/lib';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useThrottledCallback } from 'use-debounce';

const DEFAULT_PAGE_SIZE_OPTIONS_LIST = [...DEFAULT_PAGE_SIZE_OPTIONS];

interface CustomPaginationProps {
  totalItems: number;
  currentPage: number;
  itemsPerPage: number;
  showPageSizeSelector?: boolean;
  pageSizeOptions?: number[];
  pageSizeSelectorLabel?: string;
  mobileBreakpoint?: number;
  className?: string;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

const PaginationComponent = ({
  totalItems,
  currentPage,
  className,
  itemsPerPage,
  showPageSizeSelector = false,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS_LIST,
  pageSizeSelectorLabel = 'Per page',
  mobileBreakpoint = 768,
  onPageChange,
  onPageSizeChange,
}: CustomPaginationProps) => {
  // const t = useTranslations("pagination");

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const [isMobile, setIsMobile] = useState(false);

  // Create throttled handler for resize events
  const throttledResizeHandler = useThrottledCallback(() => {
    setIsMobile(window.innerWidth <= mobileBreakpoint);
  }, 1500); // Throttle to once every 1.5 seconds

  useEffect(() => {
    // Set initial state
    setIsMobile(window.innerWidth <= mobileBreakpoint);

    // Add throttled resize listener
    window.addEventListener('resize', throttledResizeHandler);
    return () => window.removeEventListener('resize', throttledResizeHandler);
  }, [throttledResizeHandler, mobileBreakpoint]);

  // Memoize page range calculations
  const { startPage, endPage } = useMemo(() => {
    let start = Math.max(2, currentPage <= 2 ? 2 : currentPage - 1);
    let end = Math.min(
      totalPages - 1,
      currentPage >= totalPages - 1 ? totalPages - 1 : currentPage + 1
    );

    if (currentPage <= 2) {
      end = Math.min(4, totalPages - 1);
    }
    if (currentPage >= totalPages - 1) {
      start = Math.max(2, totalPages - 3);
    }

    return { startPage: start, endPage: end };
  }, [currentPage, totalPages]);

  // Memoize middle pages calculation
  const middlePagesNeeded = useMemo(() => (isMobile ? 1 : 3), [isMobile]);

  const resolvedPageSizeOptions = useMemo(() => {
    if (pageSizeOptions.includes(itemsPerPage)) {
      return pageSizeOptions;
    }

    return [...pageSizeOptions, itemsPerPage].sort((a, b) => a - b);
  }, [itemsPerPage, pageSizeOptions]);

  // Memoize page change handlers
  const handlePageChange = useCallback(
    (page: number) => {
      // Ensure page is within valid bounds (1 to totalPages)
      const boundedPage = Math.min(Math.max(1, page), totalPages);
      onPageChange(boundedPage);
    },
    [onPageChange, totalPages]
  );

  const handlePageSizeChange = useCallback(
    (value: string) => {
      const pageSize = Number(value);
      if (!Number.isFinite(pageSize)) return;
      onPageSizeChange?.(pageSize);
    },
    [onPageSizeChange]
  );

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  }, [currentPage, onPageChange]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, onPageChange]);

  // Memoize mobile pages render function
  const renderMobilePages = useCallback(() => {
    // First page
    if (currentPage === 1) {
      return (
        <>
          <PaginationItem>
            <PaginationLink isActive={true} size="sm">
              1
            </PaginationLink>
          </PaginationItem>
          {totalPages > 1 && (
            <PaginationItem>
              <PaginationLink onClick={() => handlePageChange(2)} size="sm">
                2
              </PaginationLink>
            </PaginationItem>
          )}
          {totalPages > 2 && (
            <PaginationItem className="w-fit cursor-default hover:bg-transparent dark:hover:bg-transparent">
              <PaginationEllipsis />
            </PaginationItem>
          )}
          {totalPages > 2 && (
            <PaginationItem>
              <PaginationLink onClick={() => handlePageChange(totalPages)} size="sm">
                {totalPages}
              </PaginationLink>
            </PaginationItem>
          )}
        </>
      );
    }

    // Second page
    if (currentPage === 2) {
      return (
        <>
          <PaginationItem>
            <PaginationLink onClick={() => handlePageChange(1)} size="sm">
              1
            </PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationLink isActive={true} size="sm">
              2
            </PaginationLink>
          </PaginationItem>
          {totalPages > 2 && (
            <PaginationItem className="w-fit cursor-default hover:bg-transparent dark:hover:bg-transparent">
              <PaginationEllipsis />
            </PaginationItem>
          )}
          {totalPages > 2 && (
            <PaginationItem>
              <PaginationLink onClick={() => handlePageChange(totalPages)} size="sm">
                {totalPages}
              </PaginationLink>
            </PaginationItem>
          )}
        </>
      );
    }

    // Last page
    if (currentPage === totalPages) {
      return (
        <>
          <PaginationItem>
            <PaginationLink onClick={() => handlePageChange(1)} size="sm">
              1
            </PaginationLink>
          </PaginationItem>
          {totalPages > 2 && (
            <PaginationItem className="w-fit cursor-default hover:bg-transparent dark:hover:bg-transparent">
              <PaginationEllipsis />
            </PaginationItem>
          )}
          <PaginationItem>
            <PaginationLink onClick={() => handlePageChange(totalPages - 1)} size="sm">
              {totalPages - 1}
            </PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationLink isActive={true} size="sm">
              {totalPages}
            </PaginationLink>
          </PaginationItem>
        </>
      );
    }

    // Page before last
    if (currentPage === totalPages - 1) {
      return (
        <>
          <PaginationItem>
            <PaginationLink onClick={() => handlePageChange(1)} size="sm">
              1
            </PaginationLink>
          </PaginationItem>
          {totalPages > 3 && (
            <PaginationItem className="w-fit cursor-default hover:bg-transparent dark:hover:bg-transparent">
              <PaginationEllipsis />
            </PaginationItem>
          )}
          <PaginationItem>
            <PaginationLink isActive={true} size="sm">
              {currentPage}
            </PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationLink onClick={() => handlePageChange(totalPages)} size="sm">
              {totalPages}
            </PaginationLink>
          </PaginationItem>
        </>
      );
    }

    // Middle pages
    return (
      <>
        <PaginationItem>
          <PaginationLink onClick={() => handlePageChange(1)} size="sm">
            1
          </PaginationLink>
        </PaginationItem>
        <PaginationItem className="w-fit cursor-default hover:bg-transparent dark:hover:bg-transparent">
          <PaginationEllipsis />
        </PaginationItem>
        <PaginationItem>
          <PaginationLink isActive={true} size="sm">
            {currentPage}
          </PaginationLink>
        </PaginationItem>
        <PaginationItem className="w-fit cursor-default hover:bg-transparent dark:hover:bg-transparent">
          <PaginationEllipsis />
        </PaginationItem>
        <PaginationItem>
          <PaginationLink onClick={() => handlePageChange(totalPages)} size="sm">
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      </>
    );
  }, [currentPage, totalPages, handlePageChange]);

  if (totalPages === 0) {
    return null;
  }

  const hasPageSizeSelector = showPageSizeSelector && !!onPageSizeChange;

  const rangeText = (
    <p
      className={cn(
        'text-grey-800 dark:text-grey-100 cursor-default text-sm whitespace-nowrap',
        hasPageSizeSelector ? 'order-1 self-center md:order-2 md:self-auto' : 'hidden md:block'
      )}
    >
      {`${(currentPage - 1) * itemsPerPage + 1} - ${Math.min(
        currentPage * itemsPerPage,
        totalItems
      )} of ${totalItems}`}
    </p>
  );

  return (
    <div
      className={cn(
        hasPageSizeSelector
          ? 'flex w-full flex-col items-end gap-4 md:flex-row md:items-center md:justify-between md:gap-3'
          : 'flex w-full items-center justify-end gap-3',
        'align-middle',
        className
      )}
    >
      {hasPageSizeSelector ? (
        <div className="flex w-full flex-col items-end gap-2 md:w-auto md:flex-row md:items-center md:gap-3">
          <div className="order-2 flex items-center gap-2 md:order-1">
            <p className="text-grey-800 dark:text-grey-100 cursor-default text-sm whitespace-nowrap sm:block">
              {pageSizeSelectorLabel}
            </p>
            <Select value={String(itemsPerPage)} onValueChange={handlePageSizeChange}>
              <SelectTrigger
                chevronDownIconClassName="text-grey-800 dark:text-grey-100 h-4 w-4"
                aria-label={pageSizeSelectorLabel}
                hasSelected
                className="h-9 w-[74px] rounded-xl px-3 py-2 text-sm font-medium text-grey-800"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent
                className="z-1000 rounded-xl px-2 py-2"
                scrollAreaClassName="px-1 py-1"
              >
                <SelectGroup>
                  {resolvedPageSizeOptions.map((option) => (
                    <SelectItem
                      key={option}
                      value={String(option)}
                      className="min-h-9 rounded-xl text-sm my-2"
                    >
                      <p>{option}</p>
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          {rangeText}
        </div>
      ) : (
        rangeText
      )}

      <PaginationRoot>
        <PaginationContent>
          <PaginationItem className={currentPage === 1 ? 'pointer-events-none' : undefined}>
            <PaginationPrevious
              onClick={handlePreviousPage}
              aria-disabled={currentPage === 1}
              aria-label="Go to previous page"
              size="sm"
            />
          </PaginationItem>

          {isMobile ? (
            renderMobilePages()
          ) : (
            <>
              {/* First page */}
              <PaginationItem>
                <PaginationLink
                  isActive={1 === currentPage}
                  onClick={() => handlePageChange(1)}
                  size="sm"
                >
                  1
                </PaginationLink>
              </PaginationItem>

              {/* First ellipsis */}
              {startPage > 2 && (
                <PaginationItem className="w-fit cursor-default hover:bg-transparent dark:hover:bg-transparent">
                  <PaginationEllipsis />
                </PaginationItem>
              )}

              {[...Array(Math.max(middlePagesNeeded, 0))].map((_, i) => {
                const pageNumber = startPage + i;
                if (pageNumber > endPage) {
                  // Show next available page instead of ellipsis
                  const nextPage = endPage + 1;
                  if (nextPage < totalPages) {
                    return (
                      <PaginationItem key={nextPage}>
                        <PaginationLink onClick={() => handlePageChange(nextPage)} size="sm">
                          {nextPage}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  }
                  return null;
                }
                return (
                  <PaginationItem key={pageNumber}>
                    <PaginationLink
                      isActive={pageNumber === currentPage}
                      onClick={() => handlePageChange(pageNumber)}
                      size="sm"
                    >
                      {pageNumber}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              {/* Second ellipsis */}
              {endPage < totalPages - 1 && (
                <PaginationItem className="w-fit cursor-default hover:bg-transparent dark:hover:bg-transparent">
                  <PaginationEllipsis />
                </PaginationItem>
              )}

              {/* Last page */}
              {totalPages > 1 && (
                <PaginationItem>
                  <PaginationLink
                    isActive={totalPages === currentPage}
                    onClick={() => handlePageChange(totalPages)}
                    size="sm"
                  >
                    {totalPages}
                  </PaginationLink>
                </PaginationItem>
              )}
            </>
          )}

          <PaginationItem
            className={currentPage === totalPages ? 'pointer-events-none' : undefined}
          >
            <PaginationNext
              onClick={handleNextPage}
              aria-disabled={currentPage === totalPages}
              aria-label="Go to next page"
              size="sm"
            />
          </PaginationItem>
        </PaginationContent>
      </PaginationRoot>
    </div>
  );
};

export const TablePagination = React.memo(PaginationComponent);
